:host {
    @include font-p-bold;

    background-color: var(--color-background-interaction-primary);
    color: var(--color-text-light);
    display: inline-block;
    position: relative;

    .svg-icon {
        pointer-events: none;
        z-index: 2;
        left: var(--button-padding-horizontal, 8px);
        position: absolute;
        top: var(--button-padding-vertical, 12px);
        width: 24px;
    }

    ::slotted(*) {
        all: unset;
        padding: var(--button-padding-vertical, 12px) var(--button-padding-horizontal, 12px);
        box-sizing: border-box;
        color: inherit;
        cursor: pointer;
        display: block;
        outline-offset: -1px;
        transition: outline-offset 0.1s linear;
        width: 100%;
        position: relative;
        z-index: 1;
    }

    ::slotted(*:hover) {
        background-color: var(--color-overlay-primary-hover);
    }

    ::slotted(*:focus-visible) {
        outline: 2px solid var(--color-border-interaction-focus);
        outline-offset: 2px;
    }
}

:host(.has-density-mode) {
    --has-icon-padding-left: calc(var(--button-padding-horizontal) + 24px + var(--button-gap-inside));
    // 24 ist die line-height
    --button-padding-vertical: calc((var(--button-min-height) - 24px) / 2);
}

@keyframes wave {
    from {
        transform: translate3d(-100%, 0, 0);
    }
    40% {
        transform: translate3d(0, 0, 0);
    }
    60% {
        transform: translate3d(0, 0, 0);
    }
    to {
        transform: translate3d(100%, 0, 0);
    }
}

:host(.active) {
    .active-animation,
    .active-animation::before {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    .active-animation {
        overflow: hidden;

        &::before {
            content: '';
            background-color: var(--color-overlay-primary-active);
            animation: 750ms ease-in-out forwards wave;
        }
    }
}

:host(.primary[disabled]),
:host(.secondary[disabled]),
:host(.tertiary[disabled]),
:host(.positive[disabled]),
:host(.negative[disabled]),
:host(.actiontrigger[disabled]) {
    box-shadow: none;
    color: var(--color-text-disabled);
    background-color: var(--color-background-interaction-disabled-secondary);
    pointer-events: none;

    ::slotted(*) {
        cursor: auto;
    }

    ::slotted(*:hover) {
        background-color: var(--color-background-interaction-disabled-secondary);
        color: var(--color-text-disabled);
    }
}

:host(.xxs:not(.has-density-mode)) {
    @include font-size-button-xxs;

    ::slotted(*) {
        padding: 0 12px;
    }

    .svg-icon {
        left: 8px;
        position: absolute;
        top: 2px;
        width: 20px;
    }
}
:host(.xs:not(.has-density-mode)) {
    ::slotted(*) {
        padding: 4px 12px;
    }

    .svg-icon {
        left: 8px;
        position: absolute;
        top: 4px;
        width: 24px;
    }
}

:host(.s:not(.has-density-mode)) {
    ::slotted(*) {
        padding: 12px;
    }

    .svg-icon {
        left: 8px;
        position: absolute;
        top: 12px;
        width: 24px;
    }
}

:host(.m:not(.has-density-mode)) {
    @include font-size-button-m;

    ::slotted(*) {
        padding: 20px 24px;
    }

    .svg-icon {
        left: 16px;
        position: absolute;
        top: 20px;
        width: 32px;
    }
}

:host(.has-icon) {
    ::slotted(*) {
        padding-left: var(--has-icon-padding-left, 36px) !important;
    }
}

:host(.m.has-icon:not(.has-density-mode)) {
    ::slotted(*) {
        padding-left: 52px !important;
    }
}

:host(.xxs.has-icon:not(.has-density-mode)) {
    ::slotted(*) {
        padding-left: 32px !important;
    }
}

:host(.icon-only) {
    ::slotted(*) {
        height: var(--button-min-height, 48px);
        padding: var(--button-padding-horizontal, 12px);
        width: var(--button-min-height, 48px);
        color: transparent !important;
        overflow: hidden;
        user-select: none;
    }

    .svg-icon {
        left: var(--button-padding-vertical, 12px) !important;
        position: absolute;
        top: var(--button-padding-vertical, 12px);
        width: 24px;
    }
}

:host(.icon-only.xs:not(.has-density-mode)) {
    ::slotted(*) {
        height: 32px;
        padding: 4px !important;
        width: 32px;
    }

    .svg-icon {
        left: 4px !important;
        top: 4px;
        width: 24px;
    }
}

:host(.icon-only.xxs:not(.has-density-mode)) {
    ::slotted(*) {
        height: 24px;
        padding: 2px !important;
        width: 24px;
    }

    .svg-icon {
        height: 20px;
        left: 2px !important;
        top: 2px;
        width: 20px;
    }
}

:host(.icon-only.xxxs:not(.has-density-mode)) {
    ::slotted(*) {
        height: 20px;
        padding: 0 !important;
        width: 20px;
    }

    .svg-icon {
        height: 20px;
        left: 0 !important;
        top: 0;
        width: 20px;
    }
}

:host(.icon-only.m:not(.has-density-mode)) {
    ::slotted(*) {
        height: 64px;
        padding: 16px;
        width: 64px;
    }

    .svg-icon {
        left: 20px !important;
        top: 20px;
        width: 32px;
    }
}

:host(.positive) {
    background-color: var(--color-background-interaction-positive);
}

:host(.negative) {
    background-color: var(--color-background-interaction-negative);
}

:host(.secondary) {
    background-color: transparent;
    box-shadow: inset 0 0 0 1px var(--color-border-interaction-default);
    color: var(--color-text-default);

    ::slotted(*:hover) {
        background-color: var(--color-overlay-secondary-hover);
    }

    ::slotted(*:active) {
        background-color: var(--color-overlay-secondary-active);
    }
}

:host(.tertiary) {
    background-color: transparent;
    color: var(--color-text-default);

    ::slotted(*:hover) {
        background-color: var(--color-overlay-secondary-hover);
    }

    ::slotted(*:active) {
        background-color: var(--color-overlay-secondary-active);
    }
}

:host(.actiontrigger) {
    background-color: transparent;
    color: var(--color-text-default);

    ::slotted(*:hover) {
        background-color: var(--color-overlay-secondary-hover);
    }

    ::slotted(*:active) {
        background-color: var(--color-overlay-secondary-active);
    }
}
:host(.actiontrigger[disabled]) {
    background-color: transparent;

    ::slotted(*:hover) {
        background-color: transparent;
    }
}

:host(.secondary[selected]),
:host(.tertiary[selected]) {
    background-color: var(--color-background-interaction-secondary-inverted);
    color: var(--color-text-default);

    ::slotted(*:hover) {
        background-color: inherit;
    }
}

@keyframes loading-animation {
    from {
        transform: rotate3d(0, 0, 1, 0deg);
    }

    to {
        transform: rotate3d(0, 0, 1, 360deg);
    }
}

:host(.loading) {
    .svg-icon.loading {
        animation: loading-animation 1s linear infinite;
    }
}

:host(.monochrome) {
    --color-background-interaction-primary: var(--color-background-interaction-primary-monochrome);
    --color-text-light: var(--color-text-inverted);
    --color-overlay-primary-active: var(--color-overlay-primary-active-inverted);
    --color-overlay-primary-hover: var(--color-overlay-primary-hover-inverted);
}

@include mq-1 {
    :host {
        ::slotted(*) {
            text-align: center;
        }
    }
}
